import { type CSSProperties, useEffect, useState } from 'react';
import type { RadioChangeEvent } from 'antd';
import { Button, Input, Modal, Radio, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import type { TopicType } from '../../pages/topics/Topics.types.ts';
import { fetchDistributionLists } from '../../pages/distribution-lists/DistributionLists.services.ts';
import type { ConfirmModalProps } from './ConfirmModal.types.ts';
import { useTranslation } from 'react-i18next';
import api from '../../services/api.ts';
import ReactQuill from 'react-quill-new';
import './ConfirmModal.style.css';

const style: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  helplink,
  subject,
  simple,
  loadTemplate,
  validateMessage,
  regulationDraftId,
  templateType,
}: ConfirmModalProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [notifications, setNotification] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBodyHTML, setIsBodyHTML] = useState(false);
  const [group, setGroup] = useState([]);

  const { data: groups } = useQuery({
    queryKey: ['distributionLists'],
    queryFn: fetchDistributionLists,
  });

  const onChange = (e: RadioChangeEvent) => {
    setNotification(e.target.value);
  };

  const handleOk = () => {
    setIsSubmitting(true);
    if (validateMessage && text.length === 0) return;
    onConfirm(text, subject, group, notifications, isBodyHTML);
  };

  const handleCancel = () => {
    onClose();
  };

  const loadEmailTemplate = async (type: string) => {
    const template =
      type === 'publish'
        ? await api.get(`/emailTemplates/approve-publication/${regulationDraftId}`)
        : await api.get(`/emailTemplates/approve-delete/${regulationDraftId}`);
    if (template?.data) {
      setText(template?.data.bodyTemplate);
      setIsBodyHTML(true);
    }
  };

  useEffect(() => {
    if (loadTemplate) {
      loadEmailTemplate(templateType || 'publish');
    }
  }, []);

  return (
    <>
      <Modal
        open={open}
        title={title}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            {t('common.confirm')}
          </Button>,
        ]}
      >
        {!simple && (
          <p style={{ margin: '16px 0' }}>
            <Radio.Group
              style={style}
              onChange={onChange}
              value={notifications}
              options={[
                { value: false, label: t('modals.confirm-modal-without-notifications') },
                { value: true, label: t('modals.confirm-modal-with-notifications') },
              ]}
            />
          </p>
        )}
        <p>
          {message}{' '}
          {helplink ? (
            <a href="https://intranet.goethe.de/service/arbeitsgrundlagen-hilfe/Seiten/Per%20E-Mail%20%C3%BCber%20neue%2c%20ge%C3%A4nderte%20oder%20gel%C3%B6schte%20Regelungen%20informieren.aspx">
              {t('common.here')}
            </a>
          ) : (
            ''
          )}
        </p>
        {!simple && (
          <p>
            <span style={{ fontWeight: 600 }}>{t('module.distribution-lists')}</span>
            <Select
              mode="tags"
              variant="outlined"
              value={group}
              disabled={!notifications}
              style={{ width: '100%' }}
              onChange={setGroup}
              options={groups?.map((group: TopicType) => ({
                value: group.id,
                label: group.name,
              }))}
            />
          </p>
        )}
        {!simple && (
          <p>
            <span style={{ fontWeight: 600 }}>Title</span>
            <Input value={subject} disabled />
          </p>
        )}
        <p>
          <ReactQuill
            id="confirm-modal-editor"
            theme="snow"
            value={text}
            formats={[]}
            onChange={setText}
            readOnly={!notifications && !simple}
          />
          {isSubmitting && validateMessage && text.length === 0 && (
            <p style={{ margin: 0, padding: 0, color: 'red' }}>
              {t('error.validation.required-message')}
            </p>
          )}
        </p>
      </Modal>
    </>
  );
};

export default ConfirmModal;